import { toLedgerZone } from '../../../utils/dates';
import {
    CONNECTION_STATE_SOURCE_STATUS,
    ENROLLMENT_COPY,
    ENROLLMENT_DATE_FORMAT,
    ENROLLMENT_STATUS,
    ENROLLMENT_VARIANTS,
    PAY_FREQUENCY_LABELS,
    PAY_TYPE_LABELS,
    PAY_TYPES,
    USER_HEADER_COPY,
} from './user.constants';

/**
 * Pure presentation rules for the User page header (KAN-738): the enrollment
 * variants and their two non-answers, the pay-type line and the payroll-source
 * badge. They read no page state; `UserHeaderController` gathers the raw values
 * and hands them here.
 */

/** "2 payroll sources" — the meta line's slot for a multi-source member. */
export function formatPayrollSourcesCount(count) {
    const noun = count === 1 ? USER_HEADER_COPY.PAYROLL_SOURCES_SINGULAR : USER_HEADER_COPY.PAYROLL_SOURCES_PLURAL;
    return `${count} ${noun}`;
}

/** "2 Payroll sources" — the sources popover's caption. */
export function formatPayrollSourcesTitle(count) {
    const noun =
        count === 1 ? USER_HEADER_COPY.PAYROLL_SOURCES_TITLE_SINGULAR : USER_HEADER_COPY.PAYROLL_SOURCES_TITLE_PLURAL;
    return `${count} ${noun}`;
}

/**
 * An instant in the reporting zone (America/New_York) — the zone every other
 * admin date is shown in — so "same day" and the printed day never depend on
 * where the viewer happens to be.
 *
 * @param {string | Date | null | undefined} value
 * @returns {import('dayjs').Dayjs | null}
 */
export function toHeaderDay(value) {
    if (value == null || value === '') return null;
    return toLedgerZone(value);
}

/**
 * The enrollment chip, as one of {@link ENROLLMENT_VARIANTS}.
 *
 * Same day is a calendar-day comparison in the reporting zone, the same frame
 * the dates are printed in — so "Opted in and enrolled Apr 20" can never sit
 * next to two different printed days.
 *
 * `hidePayType` is set only for NOT_COMPLETED: a member who never finished
 * enrolling has no confirmed pay type, so the header must not assert one.
 *
 * @param {{ status?: string, optedInAt?: string | null, enrolledAt?: string | null } | null | undefined} enrollment
 * @returns {{ variant: string, optedIn?: string, enrolled?: string, hidePayType: boolean }}
 */
export function resolveEnrollmentChip(enrollment) {
    const status = enrollment?.status ?? ENROLLMENT_STATUS.PENDING;

    if (status === ENROLLMENT_STATUS.PENDING) {
        return { variant: ENROLLMENT_VARIANTS.HIDDEN, hidePayType: false };
    }
    if (status === ENROLLMENT_STATUS.LOADING) {
        return { variant: ENROLLMENT_VARIANTS.LOADING, hidePayType: false };
    }
    if (status === ENROLLMENT_STATUS.ERROR) {
        return { variant: ENROLLMENT_VARIANTS.UNAVAILABLE, hidePayType: false };
    }

    const optedIn = toHeaderDay(enrollment?.optedInAt);
    const enrolled = toHeaderDay(enrollment?.enrolledAt);

    if (!optedIn) {
        // Enrolled with no opt-in event: say the one date we have. The opt-in
        // is never inferred from the enrollment (a member whose first sign-in
        // was not tagged `new_account` has no opt-in date at all).
        return enrolled
            ? {
                  variant: ENROLLMENT_VARIANTS.ENROLLED_ONLY,
                  enrolled: enrolled.format(ENROLLMENT_DATE_FORMAT),
                  hidePayType: false,
              }
            : { variant: ENROLLMENT_VARIANTS.HIDDEN, hidePayType: false };
    }

    const optedInLabel = optedIn.format(ENROLLMENT_DATE_FORMAT);
    if (!enrolled) {
        return { variant: ENROLLMENT_VARIANTS.NOT_COMPLETED, optedIn: optedInLabel, hidePayType: true };
    }

    const enrolledLabel = enrolled.format(ENROLLMENT_DATE_FORMAT);
    if (optedIn.isSame(enrolled, 'day')) {
        return { variant: ENROLLMENT_VARIANTS.SAME_DAY, enrolled: enrolledLabel, hidePayType: false };
    }
    return {
        variant: ENROLLMENT_VARIANTS.DIFFERENT_DAYS,
        optedIn: optedInLabel,
        enrolled: enrolledLabel,
        hidePayType: false,
    };
}

/**
 * The text of a resolved chip, for the variants that have one.
 *
 * @param {{ variant: string, optedIn?: string, enrolled?: string }} chip
 * @returns {{ lead: string, warning?: string } | null}
 */
export function formatEnrollmentChip(chip) {
    switch (chip?.variant) {
        case ENROLLMENT_VARIANTS.SAME_DAY:
            return { lead: `${ENROLLMENT_COPY.OPTED_IN_AND_ENROLLED} ${chip.enrolled}` };
        case ENROLLMENT_VARIANTS.DIFFERENT_DAYS:
            return {
                lead: `${ENROLLMENT_COPY.OPTED_IN} ${chip.optedIn} · ${ENROLLMENT_COPY.ENROLLED} ${chip.enrolled}`,
            };
        case ENROLLMENT_VARIANTS.ENROLLED_ONLY:
            return { lead: `${ENROLLMENT_COPY.ENROLLED} ${chip.enrolled}` };
        case ENROLLMENT_VARIANTS.NOT_COMPLETED:
            return {
                lead: `${ENROLLMENT_COPY.OPTED_IN} ${chip.optedIn} · `,
                warning: ENROLLMENT_COPY.NOT_COMPLETED,
            };
        case ENROLLMENT_VARIANTS.UNAVAILABLE:
            return { lead: ENROLLMENT_COPY.UNAVAILABLE };
        default:
            return null;
    }
}

/**
 * The enrollment chip ready to render: its variant (which look), its text
 * and whether it hides the pay type.
 *
 * @param {Object} [enrollment] From `UserHeaderController.resolveHeaderEnrollment`.
 * @returns {{ variant: string, lead: string | null, warning: string | null, hidePayType: boolean }}
 */
export function buildEnrollmentChip(enrollment) {
    const chip = resolveEnrollmentChip(enrollment);
    const text = formatEnrollmentChip(chip);
    return {
        variant: chip.variant,
        lead: text?.lead ?? null,
        warning: text?.warning ?? null,
        hidePayType: chip.hidePayType,
    };
}

/**
 * `salaried` boolean → {@link PAY_TYPES}. Same source the old header's
 * `workTypeLabel` read, so the label does not change.
 *
 * @param {boolean | null | undefined} salaried
 * @returns {string | null}
 */
export function resolvePayType(salaried) {
    if (salaried === true) return PAY_TYPES.SALARIED;
    if (salaried === false) return PAY_TYPES.HOURLY;
    return null;
}

/**
 * "Salaried · Twice a month", or just "Salaried" while no frequency is known.
 * Unknown tokens are dropped rather than printed raw.
 *
 * @param {string | null | undefined} payType
 * @param {string | null | undefined} frequency
 * @returns {string | null}
 */
export function formatPayTypeLine(payType, frequency) {
    const parts = [PAY_TYPE_LABELS[payType], PAY_FREQUENCY_LABELS[frequency]].filter(Boolean);
    return parts.length ? parts.join(' · ') : null;
}

/**
 * "Paycom · Salaried · Every 2 weeks" for a popover row.
 *
 * @param {{ provider?: string | null, payType?: string | null, frequency?: string | null }} source
 * @returns {string}
 */
export function formatPayrollSourceLine(source) {
    return [source?.provider, formatPayTypeLine(source?.payType, source?.frequency)].filter(Boolean).join(' · ');
}

/**
 * A popover row's badge, as one of {@link PAYROLL_SOURCE_STATUS} or `null`,
 * from the connection's own `state` through {@link CONNECTION_STATE_SOURCE_STATUS}.
 * An unknown state gets no badge.
 *
 * @param {string | null | undefined} connectionState
 * @returns {string | null}
 */
export function resolvePayrollSourceStatus(connectionState) {
    return CONNECTION_STATE_SOURCE_STATUS[connectionState] ?? null;
}

/**
 * Raw header values → the text the components render.
 *
 * - `payTypeLine` is hidden when enrollment never finished (no confirmed
 *   pay type to assert).
 * - With two or more sources the pay-type slot becomes "N payroll sources".
 * - Each popover row gets its title (employer, else provider) and its
 *   line ("provider · pay type · frequency", or just the pay line).
 *
 * @param {{ payType?: string | null, frequency?: string | null,
 *   payrolls?: Array<Object>, sourceCount?: number, enrollment?: Object }} raw
 * @returns {{ payTypeLine: string | null, sourceCount: number,
 *   sourcesLabel: string | null, sourcesTitle: string,
 *   payrolls: Array<Object>, enrollmentChip: Object }}
 */
export function presentHeader({ payType = null, frequency = null, payrolls = [], sourceCount = 0, enrollment } = {}) {
    const enrollmentChip = buildEnrollmentChip(enrollment);
    return {
        payTypeLine: enrollmentChip.hidePayType ? null : formatPayTypeLine(payType, frequency),
        sourceCount,
        sourcesLabel: sourceCount > 1 ? formatPayrollSourcesCount(sourceCount) : null,
        sourcesTitle: formatPayrollSourcesTitle(payrolls.length),
        payrolls: payrolls.map((source) => ({
            ...source,
            title: source.employer || source.provider,
            description: source.employer
                ? formatPayrollSourceLine(source)
                : formatPayTypeLine(source.payType, source.frequency),
        })),
        enrollmentChip,
    };
}
